import React, { useCallback, useEffect, useState } from 'react';
import { ArrowRight, ExternalLink } from 'lucide-react';
import { Card } from '../../../components/ui';
import { fetchNameHistory } from '../governmentApi';
import { govDate, hostOf } from '../format';
import type { NameChangeView } from '../types';

/**
 * The names an organization has officially carried.
 *
 * Only official changes — a body renamed by decree or restructuring. Fixing
 * how a record was spelled is an edit like any other and lives in the audit
 * log, not here.
 *
 * Most records never change name, so the fetch is shared: a page that shows
 * both the card and the header note reads the history once and hands it to
 * both.
 */
export function useNameHistory(organizationId?: string) {
  const [changes, setChanges] = useState<NameChangeView[]>([]);
  const [loading, setLoading] = useState(!!organizationId);

  const load = useCallback(async () => {
    if (!organizationId) {
      setChanges([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      setChanges(await fetchNameHistory(organizationId));
    } catch {
      // A record with no history, or a server without the feature: say nothing
      setChanges([]);
    } finally {
      setLoading(false);
    }
  }, [organizationId]);

  useEffect(() => {
    void load();
  }, [load]);

  return { changes, loading, reload: load };
}

/**
 * "Ministère des Mines (MMG)" — the acronym only when it is part of what
 * changed. Repeating an acronym that stayed the same on both sides of an
 * arrow says nothing.
 */
const withAcronym = (
  name: string,
  acronym: string | null,
  other: string | null,
): string => (acronym && acronym !== other ? `${name} (${acronym})` : name);

const Dot: React.FC<{ current?: boolean }> = ({ current }) => (
  <span
    aria-hidden="true"
    className={`absolute -left-[17px] top-[5px] h-[7px] w-[7px] rounded-full ring-2 ring-white ${
      current ? 'bg-primary-600' : 'bg-neutral-300'
    }`}
  />
);

/**
 * The names this entity has been known by, newest first.
 *
 * Nothing at all when nothing has changed — which is the common case.
 */
export const NameHistory: React.FC<{
  organizationId: string;
  currentName: string;
  className?: string;
  /** Already loaded by the page, so the history is not fetched twice */
  changes?: NameChangeView[];
}> = ({ organizationId, currentName, className = '', changes }) => {
  const loaded = useNameHistory(changes ? undefined : organizationId);
  const list = changes ?? loaded.changes;

  // Defensive: an older server may still return corrections
  const official = list.filter((change) => change.kind === 'official');
  if (official.length === 0) return null;

  const since = official.find((change) => change.effectiveDate)?.effectiveDate;

  return (
    <Card padded className={className}>
      <ol className="relative space-y-4 pl-[18px]">
        <span aria-hidden="true" className="absolute inset-y-1.5 left-[3px] w-px bg-neutral-200" />

        <li className="relative">
          <Dot current />
          <p className="break-words text-[13px] text-ink">{currentName}</p>
          <p className="mt-0.5 text-[12px] tabular-nums text-neutral-400">
            {since ? `Since ${govDate(since)}` : 'Now'}
          </p>
        </li>

        {official.map((change) => {
          const date = change.effectiveDate ?? change.recordedAt.slice(0, 10);
          return (
            <li key={change.id} className="relative">
              <Dot />
              <p className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-[13px]">
                <span className="break-words text-neutral-500">
                  {withAcronym(change.previousName, change.previousAcronym, change.newAcronym)}
                </span>
                <ArrowRight className="h-3 w-3 flex-shrink-0 text-neutral-400" strokeWidth={1.75} />
                <span className="break-words text-ink">
                  {withAcronym(change.newName, change.newAcronym, change.previousAcronym)}
                </span>
              </p>
              <p className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-[12px] text-neutral-400">
                {date && <span className="tabular-nums">Until {govDate(date)}</span>}
                {change.recordedByName && <span className="min-w-0 truncate">· {change.recordedByName}</span>}
              </p>
              {(change.source || change.sourceUrl) && (
                <p className="mt-1 text-[12px]">
                  {change.sourceUrl ? (
                    <a
                      href={change.sourceUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex max-w-full items-center gap-1 text-neutral-500 underline decoration-neutral-300 underline-offset-2 hover:text-ink"
                    >
                      <span className="min-w-0 truncate">{change.source || hostOf(change.sourceUrl)}</span>
                      <ExternalLink className="h-3 w-3 flex-shrink-0" strokeWidth={1.75} />
                    </a>
                  ) : (
                    <span className="text-neutral-500">{change.source}</span>
                  )}
                </p>
              )}
              {change.note && <p className="mt-1 text-[12px] leading-snug text-neutral-400">{change.note}</p>}
            </li>
          );
        })}
      </ol>
    </Card>
  );
};

/** One quiet line under the title: the name this entity used to carry */
export const FormerNameNote: React.FC<{
  organizationId: string;
  className?: string;
  /** Already loaded by the page, so the history is not fetched twice */
  changes?: NameChangeView[];
}> = ({ organizationId, className = '', changes }) => {
  const loaded = useNameHistory(changes ? undefined : organizationId);
  const list = changes ?? loaded.changes;
  const official = list.find((change) => change.kind === 'official');

  if (!official) return null;

  return (
    <p className={`text-[12px] text-neutral-400 ${className}`}>
      Formerly “{official.previousName}”
      {official.effectiveDate ? ` until ${govDate(official.effectiveDate)}` : ''}
    </p>
  );
};
